import React, { useState, useRef, useEffect } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../lib/utils';

const MinimalDatePicker = ({ 
  value, 
  onChange, 
  placeholder = "MM/DD/YYYY",
  className,
  id,
  disabled = false,
  ...props 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewMode, setViewMode] = useState('days'); // 'days', 'months', 'years'
  const [displayMonth, setDisplayMonth] = useState(new Date().getMonth());
  const [displayYear, setDisplayYear] = useState(new Date().getFullYear());
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const calendarRef = useRef(null);

  // Parse MM/DD/YYYY to Date
  const parseDate = (dateStr) => {
    if (!dateStr || dateStr.length !== 10) return null;
    const [month, day, year] = dateStr.split('/');
    const parsed = new Date(year, month - 1, day);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  // Format Date to MM/DD/YYYY
  const formatDate = (date) => {
    if (!date || isNaN(date.getTime())) return '';
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const selectedDate = parseDate(value);

  // Initialize display to selected date or today
  useEffect(() => {
    if (selectedDate) {
      setDisplayMonth(selectedDate.getMonth());
      setDisplayYear(selectedDate.getFullYear());
    }
  }, []);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setViewMode('days');
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        setViewMode('days');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  // Handle manual input
  const handleInputChange = (e) => {
    let cleaned = e.target.value.replace(/\D/g, '');
    
    if (cleaned.length >= 2) {
      cleaned = cleaned.slice(0, 2) + '/' + cleaned.slice(2);
    }
    if (cleaned.length >= 5) {
      cleaned = cleaned.slice(0, 5) + '/' + cleaned.slice(5, 9);
    }
    
    onChange(cleaned);
  };

  const handleInputFocus = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  // Calendar logic
  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const handleDateSelect = (day) => {
    const newDate = new Date(displayYear, displayMonth, day);
    onChange(formatDate(newDate));
    setIsOpen(false);
    setViewMode('days');
  };

  const handleToday = () => {
    const today = new Date();
    onChange(formatDate(today));
    setDisplayMonth(today.getMonth());
    setDisplayYear(today.getFullYear());
    setIsOpen(false);
    setViewMode('days');
  };

  const handlePrevMonth = () => {
    if (displayMonth === 0) {
      setDisplayMonth(11);
      setDisplayYear(displayYear - 1);
    } else {
      setDisplayMonth(displayMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (displayMonth === 11) {
      setDisplayMonth(0);
      setDisplayYear(displayYear + 1);
    } else {
      setDisplayMonth(displayMonth + 1);
    }
  };

  const handleMonthSelect = (monthIndex) => {
    setDisplayMonth(monthIndex);
    setViewMode('days');
  };

  const handleYearSelect = (year) => {
    setDisplayYear(year);
    setViewMode('days');
  };

  // Render calendar grid
  const renderDays = () => {
    const daysInMonth = getDaysInMonth(displayMonth, displayYear);
    const firstDay = getFirstDayOfMonth(displayMonth, displayYear);
    const days = [];
    const today = new Date();
    const isToday = (day) => {
      return today.getDate() === day &&
             today.getMonth() === displayMonth &&
             today.getFullYear() === displayYear;
    };
    const isSelected = (day) => {
      if (!selectedDate) return false;
      return selectedDate.getDate() === day &&
             selectedDate.getMonth() === displayMonth &&
             selectedDate.getFullYear() === displayYear;
    };

    // Previous month padding
    for (let i = 0; i < firstDay; i++) {
      days.push(
        <div key={`empty-${i}`} className="h-8 w-8" />
      );
    }

    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(
        <button
          key={day}
          type="button"
          onClick={() => handleDateSelect(day)}
          className={cn(
            "h-8 w-8 rounded-full text-sm font-normal transition-all duration-150",
            "hover:bg-slate-100 dark:hover:bg-slate-800",
            "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1",
            isSelected(day) && "bg-primary text-primary-foreground hover:bg-primary/90 font-medium",
            isToday(day) && !isSelected(day) && "ring-1 ring-slate-300 dark:ring-slate-600",
            !isSelected(day) && !isToday(day) && "text-slate-700 dark:text-slate-300"
          )}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  const renderMonthSelector = () => {
    const months = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];

    return (
      <div className="grid grid-cols-3 gap-2 p-3">
        {months.map((month, index) => (
          <button
            key={month}
            type="button"
            onClick={() => handleMonthSelect(index)}
            className={cn(
              "py-2 px-3 rounded-lg text-sm font-medium transition-all duration-150",
              "hover:bg-slate-100 dark:hover:bg-slate-800",
              "focus:outline-none focus:ring-2 focus:ring-primary",
              displayMonth === index && "bg-primary text-primary-foreground"
            )}
          >
            {month}
          </button>
        ))}
      </div>
    );
  };

  const renderYearSelector = () => {
    const currentYear = new Date().getFullYear();
    const startYear = currentYear - 50;
    const years = Array.from({ length: 101 }, (_, i) => startYear + i);

    return (
      <div className="grid grid-cols-3 gap-2 p-3 max-h-64 overflow-y-auto">
        {years.map((year) => (
          <button
            key={year}
            type="button"
            onClick={() => handleYearSelect(year)}
            className={cn(
              "py-2 px-3 rounded-lg text-sm font-medium transition-all duration-150",
              "hover:bg-slate-100 dark:hover:bg-slate-800",
              "focus:outline-none focus:ring-2 focus:ring-primary",
              displayYear === year && "bg-primary text-primary-foreground"
            )}
          >
            {year}
          </button>
        ))}
      </div>
    );
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  return (
    <div ref={containerRef} className="relative">
      {/* Input Field */}
      <div className="relative flex items-center">
        <input
          ref={inputRef}
          id={id}
          type="text"
          placeholder={placeholder}
          value={value || ''}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          disabled={disabled}
          maxLength="10"
          className={cn(
            "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 pr-10 text-sm",
            "ring-offset-background placeholder:text-muted-foreground",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            "disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
          {...props}
        />
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={cn(
            "absolute right-0 h-full px-3 text-muted-foreground",
            "hover:text-foreground transition-colors",
            "focus:outline-none",
            disabled && "cursor-not-allowed opacity-50"
          )}
        >
          <CalendarIcon className="h-4 w-4" />
        </button>
      </div>

      {/* Calendar Popup - Centered Modal */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/20 dark:bg-black/40 z-[100] animate-in fade-in-0 duration-150"
            onClick={() => {
              setIsOpen(false);
              setViewMode('days');
            }}
          />
          
          {/* Calendar */}
          <div
            ref={calendarRef}
            className={cn(
              "fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2",
              "z-[101] w-[300px] rounded-xl bg-white dark:bg-slate-900",
              "shadow-xl border border-slate-200 dark:border-slate-800",
              "animate-in fade-in-0 zoom-in-95 duration-200"
            )}
          >
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-slate-200 dark:border-slate-800">
            <button
              type="button"
              onClick={handlePrevMonth}
              className="h-7 w-7 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setViewMode(viewMode === 'months' ? 'days' : 'months')}
                className="px-3 py-1 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-sm font-medium transition-colors"
              >
                {monthNames[displayMonth]}
              </button>
              <button
                type="button"
                onClick={() => setViewMode(viewMode === 'years' ? 'days' : 'years')}
                className="px-3 py-1 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-sm font-medium transition-colors"
              >
                {displayYear}
              </button>
            </div>

            <button
              type="button"
              onClick={handleNextMonth}
              className="h-7 w-7 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center transition-colors"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          {/* Body */}
          <div className="p-3">
            {viewMode === 'days' && (
              <>
                {/* Weekday headers */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
                    <div
                      key={day}
                      className="h-8 w-8 text-xs font-medium text-muted-foreground flex items-center justify-center"
                    >
                      {day}
                    </div>
                  ))}
                </div>

                {/* Days grid */}
                <div className="grid grid-cols-7 gap-1">
                  {renderDays()}
                </div>

                {/* Today button */}
                <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-800">
                  <button
                    type="button"
                    onClick={handleToday}
                    className="w-full py-2 px-3 rounded-lg text-sm font-medium text-primary hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                  >
                    Today
                  </button>
                </div>
              </>
            )}

            {viewMode === 'months' && renderMonthSelector()}
            {viewMode === 'years' && renderYearSelector()}
          </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MinimalDatePicker;
